import { useState } from 'react';
import { Search, AlertTriangle, CheckCircle, Info, Shield } from 'lucide-react';

export default function AddressLookup() {
  const [address, setAddress] = useState('');
  const [result, setResult] = useState<any>(null);

  const handleLookup = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock result
    setResult({
      address,
      riskRating: 'low',
      riskScore: 25,
      isWhitelisted: false,
      isBlacklisted: false,
      reasons: [],
      totalTransactions: 45,
      firstSeen: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    });
  };

  const getRiskColor = (rating: string) => {
    const colors = {
      safe: 'text-green-400 bg-green-500/10 border-green-500/30',
      low: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
      medium: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
      high: 'text-orange-400 bg-orange-500/10 border-orange-500/30',
      critical: 'text-red-400 bg-red-500/10 border-red-500/30',
    };
    return colors[rating as keyof typeof colors] || colors.safe;
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
          Address Lookup
        </h1>
        <p className="text-gray-400 text-lg">
          Check if an address should be blacklisted
        </p>
      </div>

      {/* Search Form */}
      <div className="p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm mb-6">
        <form onSubmit={handleLookup} className="flex gap-3">
          <input
            type="text"
            placeholder="Enter wallet address (0x...)"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            required
            className="flex-1 px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
          <button
            type="submit"
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2"
          >
            <Search size={20} />
            Lookup
          </button>
        </form>
      </div>

      {/* Result */}
      {result && (
        <div className="space-y-6">
          {/* Risk Score Card */}
          <div className="p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Risk Assessment</h2>
                <p className="text-sm text-gray-400 font-mono">{result.address}</p>
              </div>
              <div className={`px-4 py-2 rounded-lg border font-semibold ${getRiskColor(result.riskRating)}`}>
                {result.riskRating.toUpperCase()}
              </div>
            </div>

            {/* Risk Score */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-300">Risk Score</span>
                <span className="text-2xl font-bold text-white">{result.riskScore}/100</span>
              </div>
              <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 transition-all duration-500"
                  style={{ width: `${result.riskScore}%` }}
                />
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-gray-700/30">
                <div className="text-xs text-gray-400 mb-1">Transactions</div>
                <div className="text-lg font-bold text-white">{result.totalTransactions}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-700/30">
                <div className="text-xs text-gray-400 mb-1">First Seen</div>
                <div className="text-lg font-bold text-white">30d ago</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-700/30">
                <div className="text-xs text-gray-400 mb-1">Whitelisted</div>
                <div className="text-lg font-bold text-white">{result.isWhitelisted ? 'Yes' : 'No'}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-700/30">
                <div className="text-xs text-gray-400 mb-1">Blacklisted</div>
                <div className="text-lg font-bold text-white">{result.isBlacklisted ? 'Yes' : 'No'}</div>
              </div>
            </div>
          </div>

          {/* Decision Card */}
          <div className="p-6 rounded-xl bg-green-500/10 border border-green-500/30">
            <div className="flex items-start gap-4">
              <CheckCircle size={24} className="text-green-400 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-green-300 mb-2">Safe to Interact</h3>
                <p className="text-gray-300">
                  This address shows no signs of abuse. It's safe to allow transactions.
                </p>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <div className="flex items-start gap-3">
              <Info size={18} className="text-blue-400 mt-0.5" />
              <p className="text-sm text-gray-300">
                Risk scores are calculated based on 10+ abuse detection signals including transaction patterns, hop analysis, and convergence detection.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Lookup */}
      {!result && (
        <div className="p-6 rounded-xl bg-gray-800/30 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Lookup</h3>
          <div className="space-y-2">
            <button 
              onClick={() => setAddress('0xadf258b06bb075c10d9027cee6b9ab204097aa42')}
              className="w-full px-4 py-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg text-left transition-all"
            >
              <div className="flex items-center gap-3">
                <Shield size={18} className="text-green-400" />
                <div>
                  <div className="font-medium text-white">Rewards Origin</div>
                  <div className="text-sm text-gray-400 font-mono">0xadf2...7a42</div>
                </div>
              </div>
            </button>
            <button 
              onClick={() => setAddress('0xd19A1C8278D0420BFb2c825D99DC31fF86224607')}
              className="w-full px-4 py-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg text-left transition-all"
            >
              <div className="flex items-center gap-3">
                <Shield size={18} className="text-blue-400" />
                <div>
                  <div className="font-medium text-white">Main Liquidity Pool</div>
                  <div className="text-sm text-gray-400 font-mono">0xd19A...4607</div>
                </div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
